import { Raycaster, Vector2 } from "three";
import { clamp, CollisionFacet, CollisionShapeLocalTransformFacet, ControllerAxisMessage, DeleteModelMessage, InputDeviceType, convertToNumber3, convertToNumber4, MeshGroupFacet, UpdateFacetOnModelMessage, PrimitiveShape, } from "flavr-common";
import { Quaternion, Vector3, Matrix4 } from "flavr-math";
import { ControllerMovedMessage, MouseConnectedMessage, MouseDisconnectedMessage } from "@messages/controller/ControllerMessages";
import { SELECTION_STRATEGY, UpdateCollisionShapeLocalTransformMessage, CreateCollisionShapeMessage, RemoveCollisionShapeMessage } from "@messages/selection";
import { SetSelectionStrategyMessage, ClearSelectionForInputDeviceMessage } from "@messages/selection/SelectionStrategyMessages";
import { localPlayerContainer } from "@modules/localplayer/LocalPlayerContainer";
import { camera } from "@modules/rendering/camera";
import { renderManager } from "@modules/xr/RenderManager";
import { busService } from "@modules/bus";
import { modelPool, useExternalMessages } from "@modules/model";
import { ensureLocalPlayerControllersModel, getLocalPlayerClientIdxOrThrow, getPlayerMouseModelName } from "@modules/player";
import { clockService } from "@modules/xr/ClockService";
import { BUTTON_MAPPING, AXIS_MAPPING } from "./InputMapping";
import { InputDevice, ButtonState } from "./InputDevice";
import { mouseService } from "./MouseService";
const MOUSE_SELECTION_STRATEGY = {
    type: SELECTION_STRATEGY.RAY_SELECT,
    maxLength: 1000,
    visible: false
};
const tmpVector3 = new Vector3();
const VIEW_DIR = new Vector3(0, 0, -1);
const tmpQuaternion = new Quaternion();
const MOUSE_MOVE_EPSILON = 1e-5;
const MAX_DISTANCE_FOR_MOUSE_CLICK = 0.005;
const MAX_MOUSE_CLICK_DURATION = 400;
/**
 * Normally it does not make sense to enable mouse in vr mode, but for testing purpose you may enable it here
 *
 * (Note: the calculation of screen coordinates to ray is garbage then and should be adapted for stereo rendering and/or canvas zoom
 */
const ENABLE_MOUSE_IN_VR_MODE = false;
const MOUSE_GRIP_SPACE_POSITION = [0.2, 0, -0.5];
const MOUSE_GRIP_SPACE_ORIENTATION = [0, 0, 0, 1];
const MOUSE_GRIP_SPACE_SCALE = [1, 1, 1];
export class MouseInputDevice extends InputDevice {
    constructor(uid, pendingMessages, enabled) {
        var _a;
        super(uid, pendingMessages, enabled, MAX_MOUSE_CLICK_DURATION);
        this.mousePosition = new Vector2();
        // we don't have linearVelocity or angularVelocity for mice, so we use this instead
        this.zeroVelocity = [0, 0, 0];
        this.raycaster = new Raycaster();
        this.isMouseEnabled = true;
        this.lastWheelDeltaY = 0;
        this.timeOfLatestMoveMessage = 0;
        this.lastMovePosition = [Number.NaN, Number.NaN, Number.NaN];
        this.lastMoveOrientation = [Number.NaN, Number.NaN, Number.NaN, Number.NaN];
        this.lastMoveScale = Number.NaN;
        this.buttons = [];
        this.onMouseButtonDown = (event) => {
            if (document.activeElement !== document.body) {
                ;
                document.activeElement.blur();
                document.body.focus();
            }
            if (!this.isEnabled())
                return;
            if (!this.isMouseEnabled)
                return;
            if (event.button < this.buttons.length) {
                const button = this.buttons[event.button];
                this.mousePositionToNDC(event, button.mousePosOnPress);
                this.onButtonPress(button);
            }
        };
        this.onMouseButtonUp = (event) => {
            if (!this.isEnabled())
                return;
            if (!this.isMouseEnabled)
                return;
            if (event.button < this.buttons.length) {
                const button = this.buttons[event.button];
                this.onButtonRelease(button);
            }
        };
        this.onMouseMoved = (event) => {
            if (!this.isEnabled())
                return;
            if (!this.isMouseEnabled)
                return;
            this.mousePositionToNDC(event, this.mousePosition);
            this.raycaster.setFromCamera(this.mousePosition, camera.perspectiveCam);
            this.pushAndSendMoveMessages();
        };
        this.onMouseWheel = (event) => {
            this.lastWheelDeltaY = clamp(event.deltaY, -1, 1);
        };
        renderManager.isXRPresenting$.add((isPresenting) => {
            this.onIsPresentingChanged(isPresenting);
        });
        mouseService.down$.add(this.onMouseButtonDown);
        mouseService.up$.add(this.onMouseButtonUp);
        mouseService.move$.add(this.onMouseMoved);
        mouseService.wheel$.add(this.onMouseWheel);
        const mapping = BUTTON_MAPPING[InputDeviceType.Mouse];
        for (let i = 0; i < 3; ++i) {
            this.buttons.push({
                type: mapping !== undefined ? mapping[`${i}`] : undefined,
                state: ButtonState.RELEASED,
                lastChangeTimeStamp: 0,
                doubleClick: false,
                mousePosOnPress: new Vector2()
            });
        }
        const wheelMapping = AXIS_MAPPING[InputDeviceType.Mouse][0];
        this.wheel = {
            type: wheelMapping !== undefined ? wheelMapping.type : undefined,
            invert: wheelMapping !== undefined ? (_a = wheelMapping.invert) !== null && _a !== void 0 ? _a : false : false,
            value: 0
        };
    }
    createModel() {
        this.ensureMouseModelExists();
        const positionData = MOUSE_GRIP_SPACE_POSITION;
        const orientationData = MOUSE_GRIP_SPACE_ORIENTATION;
        const scaleData = MOUSE_GRIP_SPACE_SCALE;
        const transform = new Matrix4().compose(new Vector3().fromArray(positionData), new Quaternion().fromArray(orientationData), new Vector3().fromArray(scaleData));
        const colliderScale = new Vector3().setScalar(0.1);
        const colliderTransform = new Matrix4().scale(colliderScale);
        const mouseDeviceModel = modelPool.getModelOrThrow(this.uid);
        mouseDeviceModel.setTransformComponents(positionData, orientationData, scaleData);
        mouseDeviceModel.addMessage(new SetSelectionStrategyMessage(this.uid, [0, 0, 0], [0, 0, 0, 1], MOUSE_SELECTION_STRATEGY));
        mouseDeviceModel.setFacet(new MeshGroupFacet());
        mouseDeviceModel.setFacet(new CollisionFacet(PrimitiveShape.Box, false, true));
        mouseDeviceModel.setFacet(new CollisionShapeLocalTransformFacet(colliderTransform.toArray()));
        mouseDeviceModel.addMessages([
            new CreateCollisionShapeMessage(PrimitiveShape.Box, this.uid, false, true),
            new UpdateCollisionShapeLocalTransformMessage(this.uid, colliderTransform.toArray()),
        ]);
        mouseDeviceModel.setTransform(transform);
    }
    onIsPresentingChanged(isPresenting) {
        this.isMouseEnabled = !isPresenting || ENABLE_MOUSE_IN_VR_MODE;
        if (!this.isMouseEnabled) {
            this._pendingMessages.push(new ClearSelectionForInputDeviceMessage(this.uid));
            this._pendingMessages.push(new MouseDisconnectedMessage(this.uid));
            this._pendingMessages.push(new RemoveCollisionShapeMessage(this.uid));
            if (modelPool.getModel(this.uid) !== undefined) {
                this._pendingMessages.push(new DeleteModelMessage(this.uid));
            }
        }
        else {
            this.createModel();
            this._pendingMessages.push(new MouseConnectedMessage(this.uid));
            this.ensureMouseCollisionShape(true);
        }
    }
    ensureMouseModelExists() {
        if (modelPool.getModel(this.uid) !== undefined)
            return;
        const controllerRoot = ensureLocalPlayerControllersModel();
        const mouseDeviceId = getPlayerMouseModelName(getLocalPlayerClientIdxOrThrow());
        modelPool.createModel(mouseDeviceId, controllerRoot, Object.assign({ uid: this.uid }, useExternalMessages(this._pendingMessages)));
    }
    pushAndSendMoveMessages() {
        this.sendMoveMessage(false, true);
        for (const button of this.buttons) {
            if (button.state === ButtonState.PRESSED) {
                const dX = Math.abs(button.mousePosOnPress.x - this.mousePosition.x);
                const dY = Math.abs(button.mousePosOnPress.y - this.mousePosition.y);
                if (dX > MAX_DISTANCE_FOR_MOUSE_CLICK || dY > MAX_DISTANCE_FOR_MOUSE_CLICK) {
                    this.onButtonHold(button);
                }
            }
        }
    }
    sendMoveMessage(forceLocalOnly, forceSend = false) {
        const position = tmpVector3.copy(this.raycaster.ray.origin);
        const rayDir = this.raycaster.ray.direction.normalize();
        const orientation = tmpQuaternion.setFromUnitVectors(VIEW_DIR, rayDir);
        const positionArray = convertToNumber3(position);
        const orientationArray = convertToNumber4(orientation);
        const scale = localPlayerContainer.getPlatformScale();
        const positionChanged = Math.abs(positionArray[0] - this.lastMovePosition[0]) >= MOUSE_MOVE_EPSILON ||
            Math.abs(positionArray[1] - this.lastMovePosition[1]) >= MOUSE_MOVE_EPSILON ||
            Math.abs(positionArray[2] - this.lastMovePosition[2]) >= MOUSE_MOVE_EPSILON;
        const orientationChanged = Math.abs(orientationArray[0] - this.lastMoveOrientation[0]) >= MOUSE_MOVE_EPSILON ||
            Math.abs(orientationArray[1] - this.lastMoveOrientation[1]) >= MOUSE_MOVE_EPSILON ||
            Math.abs(orientationArray[2] - this.lastMoveOrientation[2]) >= MOUSE_MOVE_EPSILON ||
            Math.abs(orientationArray[3] - this.lastMoveOrientation[3]) >= MOUSE_MOVE_EPSILON;
        const scaleChanged = Math.abs(scale - this.lastMoveScale) >= MOUSE_MOVE_EPSILON;
        if (!(positionChanged || orientationChanged || scaleChanged || forceLocalOnly || forceSend))
            return;
        this.timeOfLatestMoveMessage = clockService.lastUpdateTime;
        this.lastMovePosition[0] = positionArray[0];
        this.lastMovePosition[1] = positionArray[1];
        this.lastMovePosition[2] = positionArray[2];
        this.lastMoveOrientation[0] = orientationArray[0];
        this.lastMoveOrientation[1] = orientationArray[1];
        this.lastMoveOrientation[2] = orientationArray[2];
        this.lastMoveOrientation[3] = orientationArray[3];
        this.lastMoveScale = scale;
        const moveMessage = new ControllerMovedMessage(this.uid, positionArray, orientationArray, scale, this.zeroVelocity);
        if (forceLocalOnly) {
            busService.connection("InputDevice").sendLocal(moveMessage, clockService.lastUpdateTime, `${ControllerMovedMessage.subjectstatic}/${ControllerMovedMessage.typestatic}`);
        }
        else {
            this._pendingMessages.push(moveMessage);
        }
    }
    update() {
        if (!this.isEnabled())
            return;
        if (!this.isMouseEnabled)
            return;
        if (this.wheel.value !== this.lastWheelDeltaY) {
            this.wheel.value = this.lastWheelDeltaY;
            if (this.wheel.type !== undefined) {
                this._pendingMessages.push(new ControllerAxisMessage(this.uid, this.wheel.type, this.wheel.value));
            }
        }
        this.lastWheelDeltaY = 0;
        this.sendOccasionalMoveMessage();
        super.update();
    }
    /**
     * All other types of controllers send a movement-update every frame. to make sure selection is updated properly, we
     * send a fake move-message from time to time:
     */
    sendOccasionalMoveMessage() {
        if (this.timeOfLatestMoveMessage !== 0 && clockService.lastUpdateTime - this.timeOfLatestMoveMessage > 200) {
            this.sendMoveMessage(true);
        }
    }
    updateButtonMapping(buttonId, type) {
        const buttonIndex = parseInt(buttonId);
        if (!isNaN(buttonIndex) && buttonIndex >= 0 && buttonIndex < this.buttons.length) {
            this.onButtonTypeChanged(this.buttons[buttonIndex], type);
        }
    }
    forEachButton(callback) {
        for (const button of this.buttons)
            callback(button);
    }
    /**
     * calculate mouse position in normalized device coordinates
     * (-1 to +1) for both components
     * @param event
     * @param pos Is used instead of a return-value to store the calculated position.
     */
    mousePositionToNDC(event, pos) {
        const worldContainer = document.getElementById("world-container");
        if (worldContainer !== null) {
            // event.clientX/event.clientY are viewport coordinates, so we use world-containers bounding rect to make them relative to the container
            const rect = worldContainer === null || worldContainer === void 0 ? void 0 : worldContainer.getBoundingClientRect();
            pos.x = ((event.clientX - rect.x) / worldContainer.offsetWidth) * 2 - 1;
            pos.y = -((event.clientY - rect.y) / worldContainer.offsetHeight) * 2 + 1;
        }
        else {
            pos.x = (event.clientX / window.innerWidth) * 2 - 1;
            pos.y = -(event.clientY / window.innerHeight) * 2 + 1;
        }
    }
    ensureMouseCollisionShape(forceRecreate) {
        const colliderTransform = new Matrix4().scale(new Vector3().setScalar(0.1));
        const mouseModel = modelPool.getModel(this.uid);
        if (mouseModel === undefined) {
            return;
        }
        if (mouseModel.getFacet(CollisionFacet) === undefined) {
            this._pendingMessages.push(new UpdateFacetOnModelMessage(this.uid, new CollisionFacet(PrimitiveShape.Box, false, true)));
        }
        this._pendingMessages.push(new UpdateFacetOnModelMessage(this.uid, new CollisionShapeLocalTransformFacet(colliderTransform.toArray())));
        if (forceRecreate) {
            this._pendingMessages.push(new RemoveCollisionShapeMessage(this.uid));
        }
        this._pendingMessages.push(new CreateCollisionShapeMessage(PrimitiveShape.Box, this.uid, false, true));
        this._pendingMessages.push(new UpdateCollisionShapeLocalTransformMessage(this.uid, colliderTransform.toArray()));
    }
}
